import clsx from "clsx";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { capitalize } from "lodash-es";
import { FC, ReactElement, ReactNode, useRef } from "react";
import DatePicker, { ReactDatePickerCustomHeaderProps, registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { Button } from "@/components/common/Button";
import { Icon } from "@/components/common/Icon";
import { LoadingSkeleton } from "@/components/common/LoadingSkeleton";
import { InputError } from "../InputError";
import { useInput } from "@/hooks/common/use-input";
import { CommonInputProps } from "@/interfaces/form";
import "./styles.css";

interface Props extends CommonInputProps {
  minDate?: Date;
  maxDate?: Date;
  ranged?: true;
  startDate?: Date;
  endDate?: Date;
  showIcon?: boolean;
  disabledDaysAfter?: boolean;
  hideRemoveFilterButton?: boolean;
  popperContainer?: FC<{ children?: ReactNode }>;
  popperClassName?: string;
  popperPlacement?: "top-start" | "bottom-start";
  rightContent?: ReactNode;
  customInput?: ReactElement;
  applyLabel?: string;
  onApply?: () => void;
}

registerLocale("pt-BR", ptBR);

export const InputDatePicker = ({
  control,
  label,
  field,
  minDate,
  maxDate,
  startDate,
  endDate,
  showIcon = true,
  disabledDaysAfter = false,
  hideRemoveFilterButton = false,
  popperContainer,
  popperClassName,
  popperPlacement = "top-start",
  rightContent,
  customInput,
  applyLabel = "Aplicar filtro",
  onApply,
  ranged,
  placeholder,
  className,
  disabled,
}: Props): JSX.Element => {
  const currentValue = control.watch(field);
  const [currentStartDate, currentEndDate] =
    ranged && Array.isArray(currentValue) ? currentValue : [startDate, endDate];
  const datePickerRef = useRef<DatePicker>(null);

  const { errorMessage } = useInput({ control, field });

  const handleOnChange = (value: Date | [Date, Date] | null) => {
    control.onChange({
      target: { name: field, value },
    });
  };

  const handleClosePickerDate = () => {
    datePickerRef.current?.setOpen(false);
  };

  const handleRemoveFilter = () => {
    control.setValue(field, null);
  };

  return (
    <fieldset className={clsx("flex w-full flex-col", className)}>
      {label && (
        <label className={clsx("label", { "opacity-50": disabled })} htmlFor={field}>
          {label}
        </label>
      )}

      <div className="relative flex w-full flex-col">
        <DatePicker
          id={field}
          ref={datePickerRef}
          locale="pt-BR"
          dateFormat="dd/MM/yyyy"
          selected={!ranged ? currentValue : undefined}
          formatWeekDay={(nameOfDay) => nameOfDay[0]}
          placeholderText={placeholder}
          disabled={disabled}
          renderCustomHeader={CalendarHeader}
          minDate={minDate}
          maxDate={disabledDaysAfter ? new Date() : maxDate}
          startDate={currentStartDate}
          endDate={currentEndDate}
          className={clsx("input-date-picker pl-4", {
            "!pl-10": showIcon,
            "!pr-40": rightContent,
            "!border-error": !!errorMessage,
          })}
          calendarClassName="date-picker-calendar"
          popperClassName={clsx("date-picker-popper", popperClassName)}
          onChange={handleOnChange}
          showPopperArrow={false}
          selectsRange={ranged as any}
          showIcon={showIcon}
          icon={<Icon name="Calendar" />}
          popperPlacement={popperPlacement}
          popperContainer={popperContainer}
          customInput={customInput}
          toggleCalendarOnIconClick
          shouldCloseOnSelect={false}
        >
          <div className="flex w-full flex-col gap-y-5 p-4 pt-6">
            {!hideRemoveFilterButton && (
              <Button onClick={handleRemoveFilter} variant="text" textClassName="text-error text-sm">
                Remover filtro
              </Button>
            )}

            <div className="flex w-full justify-between gap-2">
              <Button onClick={handleClosePickerDate} variant="secondary" textClassName="text-sm" fit>
                Fechar
              </Button>
              <Button
                onClick={() => {
                  onApply?.();
                  handleClosePickerDate();
                }}
                variant="primary"
                textClassName="text-sm"
                fit
              >
                {applyLabel}
              </Button>
            </div>
          </div>
        </DatePicker>

        {rightContent && (
          <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2">{rightContent}</div>
        )}

        {control.isFetching && <LoadingSkeleton kind="absolute" />}
        {!!errorMessage && <InputError message={errorMessage} />}
      </div>
    </fieldset>
  );
};

const CalendarHeader = ({ date, increaseMonth, decreaseMonth }: ReactDatePickerCustomHeaderProps) => {
  const yearMonth = capitalize(format(date, "MMMM '|' yyyy", { locale: ptBR }));

  return (
    <div className="ml-5 flex items-center justify-between pb-2">
      <span className="text-sm font-bold">{yearMonth}</span>

      <div>
        <Icon className="select-none px-2" name="KeyboardArrowLeft" onClick={decreaseMonth} size="1rem" />

        <Icon className="select-none px-2" name="KeyboardArrowRight" onClick={increaseMonth} size="1rem" />
      </div>
    </div>
  );
};
